import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase, Megaphone, UserCheck, Calendar, ArrowRight, Plus, UserCog, Activity, MapPin,
} from "lucide-react";
import api from "@/lib/api";
import { Spinner } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { timeAgo } from "@/pages/Opportunities";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="container-nx section-nx"><Spinner /></div>;

  const pc = data.profile_completion;

  return (
    <div className="container-nx section-nx">
      <h1 className="heading-2">Welcome back, {user.name.split(" ")[0]}</h1>
      <p className="mt-2 text-base text-slate-500">Here's what's happening in your community.</p>

      {/* Quick actions */}
      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Link to="/profile/edit" className="card-nx card-nx-hover flex flex-col items-center gap-2 p-5 text-center" data-testid="quick-edit-profile">
          <UserCog className="h-6 w-6 text-[#B8860B]" />
          <span className="text-sm font-semibold text-[#0B192C]">Edit Profile</span>
        </Link>
        <Link to="/my/businesses" className="card-nx card-nx-hover flex flex-col items-center gap-2 p-5 text-center" data-testid="quick-my-businesses">
          <Briefcase className="h-6 w-6 text-[#B8860B]" />
          <span className="text-sm font-semibold text-[#0B192C]">My Businesses</span>
        </Link>
        <Link to="/opportunities" className="card-nx card-nx-hover flex flex-col items-center gap-2 p-5 text-center" data-testid="quick-post-opportunity">
          <Megaphone className="h-6 w-6 text-[#B8860B]" />
          <span className="text-sm font-semibold text-[#0B192C]">Opportunities</span>
        </Link>
        <Link to="/connections" className="card-nx card-nx-hover flex flex-col items-center gap-2 p-5 text-center" data-testid="quick-connections">
          <UserCheck className="h-6 w-6 text-[#B8860B]" />
          <span className="text-sm font-semibold text-[#0B192C]">Connections</span>
        </Link>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Profile completion */}
          <div className="card-nx p-6" data-testid="profile-completion-card">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#0B192C] font-head">Profile completion</h3>
              <span className="text-sm font-bold text-[#B8860B]">{pc}%</span>
            </div>
            <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-[#D4AF37] transition-all" style={{ width: `${pc}%` }} />
            </div>
            {pc < 100 && (
              <Link to="/profile/edit" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#0B192C] hover:underline" data-testid="complete-profile-link">
                Complete your profile <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>

          {/* My businesses */}
          <div className="card-nx p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#0B192C] font-head">My businesses</h3>
              <Link to="/my/businesses" className="text-xs font-medium text-[#B8860B] hover:underline">Manage</Link>
            </div>
            {data.businesses.length === 0 ? (
              <p className="mt-4 text-sm text-slate-400">You haven't added any businesses yet. <Link to="/my/businesses" className="font-medium text-[#0B192C] hover:underline">Add one</Link>.</p>
            ) : (
              <div className="mt-4 space-y-2">
                {data.businesses.map((b) => (
                  <Link key={b.id} to={`/businesses/${b.id}`} className="flex items-center justify-between rounded-lg border border-slate-100 p-3 hover:bg-[#F5F3EF]" data-testid={`dash-business-${b.id}`}>
                    <span className="text-sm font-medium text-[#0B192C]">{b.name}</span>
                    <span className="text-xs text-slate-400">{b.city}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* My opportunities */}
          <div className="card-nx p-6">
            <h3 className="font-semibold text-[#0B192C] font-head">My opportunities</h3>
            {data.opportunities.length === 0 ? (
              <p className="mt-4 text-sm text-slate-400">No posts yet. Share a requirement or referral from the <Link to="/opportunities" className="font-medium text-[#0B192C] hover:underline">Opportunities</Link> page.</p>
            ) : (
              <div className="mt-4 space-y-2">
                {data.opportunities.map((o) => (
                  <Link key={o.id} to={`/opportunities/${o.id}`} className="flex items-center justify-between rounded-lg border border-slate-100 p-3 hover:bg-[#F5F3EF]" data-testid={`dash-opp-${o.id}`}>
                    <span className="truncate text-sm font-medium text-[#0B192C]">{o.title}</span>
                    <span className="shrink-0 text-xs text-slate-400">{timeAgo(o.created_at)}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Pending requests */}
          <div className="card-nx p-6" data-testid="dash-pending-requests">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#0B192C] font-head">Connection requests</h3>
              <Link to="/connections" className="text-xs font-medium text-[#B8860B] hover:underline">View all</Link>
            </div>
            {data.pending_requests.length === 0 ? (
              <p className="mt-4 text-sm text-slate-400">No pending requests.</p>
            ) : (
              <div className="mt-4 space-y-2">
                {data.pending_requests.map((r) => (
                  <div key={r.id} className="rounded-lg border border-slate-100 p-3">
                    <p className="text-sm font-medium text-[#0B192C]">{r.from_user_name}</p>
                    <p className="text-xs text-slate-400">wants to connect</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming events */}
          <div className="card-nx p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#0B192C] font-head">Upcoming events</h3>
              <Link to="/events" className="text-xs font-medium text-[#B8860B] hover:underline">View all</Link>
            </div>
            {data.upcoming_events.length === 0 ? (
              <p className="mt-4 text-sm text-slate-400">No upcoming events.</p>
            ) : (
              <div className="mt-4 space-y-2">
                {data.upcoming_events.map((e) => (
                  <Link key={e.id} to="/events" className="flex items-start gap-2 rounded-lg border border-slate-100 p-3 hover:bg-[#F5F3EF]">
                    <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-[#B8860B]" />
                    <div>
                      <p className="text-sm font-medium text-[#0B192C]">{e.title}</p>
                      <p className="text-xs text-slate-400">{new Date(e.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} · {e.location}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Recent activity */}
          <div className="card-nx p-6">
            <h3 className="flex items-center gap-2 font-semibold text-[#0B192C] font-head"><Activity className="h-4 w-4 text-[#B8860B]" />Recent activity</h3>
            {data.recent_activity.length === 0 ? (
              <p className="mt-4 text-sm text-slate-400">No recent activity.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {data.recent_activity.map((o) => (
                  <Link key={o.id} to={`/opportunities/${o.id}`} className="block">
                    <p className="text-sm font-medium text-[#0B192C] hover:underline">{o.title}</p>
                    <p className="flex items-center gap-1 text-xs text-slate-400">
                      {o.category}{o.location && <><MapPin className="ml-1 h-3 w-3" />{o.location}</>}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
